import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  CheckOutlined,
  ClearOutlined,
  CloseCircleOutlined,
  DownOutlined,
  FilterOutlined,
  LockOutlined,
  MenuOutlined,
  ReloadOutlined,
  ShopOutlined,
  TagOutlined,
  TruckOutlined,
  UpOutlined,
  UserOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import { DEFAULT_FILTER_VALUES, FILTER_TYPES } from '../constants/filterTypes';
import { FilterManager } from '../utils/filterManager';
import SearchInput from './SearchInput';
import api from '../../../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;
const ShopifyOrdersFilter = ({
  onFilterChange,
  onClearFilters,
  onApplyFilters,
  onReload,
  filters,
  loading,
  lockedEmployeeId = null
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [shopifyCollections, setShopifyCollections] = useState([]);

  const { shopifyStores = [], shopifyCategories = [], printers = [], users = [] } = useSelector(
    (state) => state.dataCommon
  );

  useEffect(() => {
    let isMounted = true;

    const fetchCollections = async () => {
      try {
        const pageSize = 250;
        let page = 1;
        let totalPages = 1;
        const collections = [];

        do {
          const response = await api.get('/shopify/categories/collections', {
            params: {
              page,
              pageSize,
              status: 'active',
              sortBy: 'title',
              sortOrder: 'ASC'
            }
          });

          collections.push(...(response.data?.rows || []));
          totalPages = response.data?.pagination?.totalPages || 1;
          page += 1;
        } while (page <= totalPages);

        if (isMounted) {
          setShopifyCollections(collections);
        }
      } catch (error) {
        console.error('Error fetching Shopify collections for order filter:', error);
      }
    };

    fetchCollections();

    return () => {
      isMounted = false;
    };
  }, []);

  // Extract filter values - explicitly handle selectedDateRange to preserve null
  const {
    searchText = DEFAULT_FILTER_VALUES[FILTER_TYPES.SEARCH],
    selectedStore = DEFAULT_FILTER_VALUES[FILTER_TYPES.STORE],
    selectedFinancialStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.FINANCIAL_STATUS],
    selectedFulfillmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.FULFILLMENT_STATUS],
    selectedShipmentStatus = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_STATUS],
    selectedShipmentDetail = DEFAULT_FILTER_VALUES[FILTER_TYPES.SHIPMENT_DETAIL],
    selectedConfirmShipment = DEFAULT_FILTER_VALUES[FILTER_TYPES.CONFIRM_SHIPMENT],
    selectedCategory = DEFAULT_FILTER_VALUES[FILTER_TYPES.CATEGORY],
    selectedCollection = DEFAULT_FILTER_VALUES[FILTER_TYPES.COLLECTION],
    selectedPrinter = DEFAULT_FILTER_VALUES[FILTER_TYPES.PRINTER],
    selectedSupporter = DEFAULT_FILTER_VALUES[FILTER_TYPES.SUPPORTER],
    selectedTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.TITLE],
    selectedSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.SKU],
    selectedEmployeeId = DEFAULT_FILTER_VALUES[FILTER_TYPES.EMPLOYEE_ID],
    selectedExcludeTitle = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_TITLE],
    selectedExcludeSku = DEFAULT_FILTER_VALUES[FILTER_TYPES.EXCLUDE_SKU],
    selectedBasecost = DEFAULT_FILTER_VALUES[FILTER_TYPES.BASECOST]
  } = filters || {};
  
  // For selectedDateRange, explicitly check for undefined to preserve null after clear
  const selectedDateRange = filters?.selectedDateRange !== undefined 
    ? filters.selectedDateRange 
    : DEFAULT_FILTER_VALUES[FILTER_TYPES.DATE_RANGE];

  const toArray = (v) => Array.isArray(v) ? v : (v && v !== 'all' ? [v] : []);

  const [localFilters, setLocalFilters] = useState({
    searchText,
    selectedDateRange,
    selectedStore: toArray(selectedStore),
    selectedFinancialStatus: toArray(selectedFinancialStatus),
    selectedFulfillmentStatus: toArray(selectedFulfillmentStatus),
    selectedShipmentStatus: toArray(selectedShipmentStatus),
    selectedShipmentDetail,
    selectedConfirmShipment,
    selectedCategory: toArray(selectedCategory),
    selectedCollection: toArray(selectedCollection),
    selectedPrinter: toArray(selectedPrinter),
    selectedSupporter: toArray(selectedSupporter),
    selectedTitle,
    selectedSku,
    selectedEmployeeId,
    selectedExcludeTitle,
    selectedExcludeSku,
    selectedBasecost
  });

  useEffect(() => {
    setLocalFilters({
      searchText,
      selectedDateRange,
      selectedStore: toArray(selectedStore),
      selectedFinancialStatus: toArray(selectedFinancialStatus),
      selectedFulfillmentStatus: toArray(selectedFulfillmentStatus),
      selectedShipmentStatus: toArray(selectedShipmentStatus),
      selectedShipmentDetail,
      selectedConfirmShipment,
      selectedCategory: toArray(selectedCategory),
      selectedCollection: toArray(selectedCollection),
      selectedPrinter: toArray(selectedPrinter),
      selectedSupporter: toArray(selectedSupporter),
      selectedTitle,
      selectedSku,
      selectedEmployeeId,
      selectedExcludeTitle,
      selectedExcludeSku,
      selectedBasecost
    });
  }, [
    searchText,
    selectedDateRange,
    selectedStore,
    selectedFinancialStatus,
    selectedFulfillmentStatus,
    selectedShipmentStatus,
    selectedShipmentDetail,
    selectedConfirmShipment,
    selectedCategory,
    selectedCollection,
    selectedPrinter,
    selectedSupporter,
    selectedTitle,
    selectedSku,
    selectedEmployeeId,
    selectedExcludeTitle,
    selectedExcludeSku,
    selectedBasecost
  ]);

  const handleLocalChange = useCallback((key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleShipmentDetailChange = useCallback((value) => {
    setLocalFilters((prev) => ({
      ...prev,
      selectedShipmentDetail: value,
      selectedShipmentStatus: value === 'stale_over_3_days' ? ['in_transit'] : prev.selectedShipmentStatus
    }));
  }, []);

  // Convert newline-separated search text to comma-separated format for API calls
  const getSearchTextForAPI = useCallback((text) => {
    if (!text) return '';
    if (text.includes('\n')) {
      return text.split('\n').map((item) => item.trim()).filter((item) => item.length > 0).join(', ');
    }
    return text;
  }, []);

  const handleSearchExecute = useCallback((apiSearchText) => {
    onFilterChange(FILTER_TYPES.SEARCH, apiSearchText);
    onApplyFilters();
  }, [onFilterChange, onApplyFilters]);

  const handleClearSearch = useCallback(() => {
    handleLocalChange('searchText', '');
    onFilterChange(FILTER_TYPES.SEARCH, '');
    onApplyFilters();
    onReload?.({ preserveSelection: false });
  }, [handleLocalChange, onFilterChange, onApplyFilters, onReload]);

  const handleApplyFilters = useCallback(() => {
    const apiSearchText = getSearchTextForAPI(localFilters.searchText);
    onFilterChange(FILTER_TYPES.SEARCH, apiSearchText);
    onFilterChange(FILTER_TYPES.DATE_RANGE, localFilters.selectedDateRange);
    onFilterChange(FILTER_TYPES.STORE, localFilters.selectedStore);
    onFilterChange(FILTER_TYPES.FINANCIAL_STATUS, localFilters.selectedFinancialStatus);
    onFilterChange(FILTER_TYPES.FULFILLMENT_STATUS, localFilters.selectedFulfillmentStatus);
    onFilterChange(FILTER_TYPES.SHIPMENT_STATUS, localFilters.selectedShipmentStatus);
    onFilterChange(FILTER_TYPES.SHIPMENT_DETAIL, localFilters.selectedShipmentDetail || 'all');
    onFilterChange(FILTER_TYPES.CONFIRM_SHIPMENT, localFilters.selectedConfirmShipment || 'all');
    onFilterChange(FILTER_TYPES.CATEGORY, localFilters.selectedCategory);
    onFilterChange(FILTER_TYPES.COLLECTION, localFilters.selectedCollection);
    onFilterChange(FILTER_TYPES.PRINTER, localFilters.selectedPrinter);
    onFilterChange(FILTER_TYPES.SUPPORTER, localFilters.selectedSupporter);
    onFilterChange(FILTER_TYPES.TITLE, (localFilters.selectedTitle || '').trim());
    onFilterChange(FILTER_TYPES.SKU, (localFilters.selectedSku || '').trim());
    onFilterChange(FILTER_TYPES.EMPLOYEE_ID, (localFilters.selectedEmployeeId || '').trim());
    onFilterChange(FILTER_TYPES.EXCLUDE_TITLE, (localFilters.selectedExcludeTitle || '').trim());
    onFilterChange(FILTER_TYPES.EXCLUDE_SKU, (localFilters.selectedExcludeSku || '').trim());
    onFilterChange(FILTER_TYPES.BASECOST, localFilters.selectedBasecost || 'all');
    onApplyFilters();
  }, [localFilters, onApplyFilters, onFilterChange, getSearchTextForAPI]);

  const handleClearFilters = useCallback(() => {
    // Clear Redux state first, then let useEffect sync to local state
    onClearFilters();
  }, [onClearFilters]);

  const hasUnappliedChanges = useMemo(() => {
    return FilterManager.hasUnappliedChanges(localFilters, filters);
  }, [localFilters, filters]);

  const activeFiltersCount = useMemo(() => {
    return FilterManager.countActiveFilters(filters, { lockedEmployeeId });
  }, [filters, lockedEmployeeId]);

  const FILTER_TYPE_MAP = useMemo(() => ({
    searchText: FILTER_TYPES.SEARCH,
    selectedDateRange: FILTER_TYPES.DATE_RANGE,
    selectedStore: FILTER_TYPES.STORE,
    selectedFinancialStatus: FILTER_TYPES.FINANCIAL_STATUS,
    selectedFulfillmentStatus: FILTER_TYPES.FULFILLMENT_STATUS,
    selectedShipmentStatus: FILTER_TYPES.SHIPMENT_STATUS,
    selectedShipmentDetail: FILTER_TYPES.SHIPMENT_DETAIL,
    selectedConfirmShipment: FILTER_TYPES.CONFIRM_SHIPMENT,
    selectedCategory: FILTER_TYPES.CATEGORY,
    selectedCollection: FILTER_TYPES.COLLECTION,
    selectedPrinter: FILTER_TYPES.PRINTER,
    selectedSupporter: FILTER_TYPES.SUPPORTER,
    selectedTitle: FILTER_TYPES.TITLE,
    selectedSku: FILTER_TYPES.SKU,
    selectedEmployeeId: FILTER_TYPES.EMPLOYEE_ID,
    selectedExcludeTitle: FILTER_TYPES.EXCLUDE_TITLE,
    selectedExcludeSku: FILTER_TYPES.EXCLUDE_SKU,
    selectedBasecost: FILTER_TYPES.BASECOST
  }), []);

  const clearIndividualFilter = useCallback((filterKey) => {
    const defaults = FilterManager.getDefaultFilters();
    const clearValue = defaults[filterKey];
    handleLocalChange(filterKey, clearValue);
  }, [handleLocalChange]);

  const handleRemoveFilterTag = useCallback((filterKey) => {
    const defaults = FilterManager.getDefaultFilters();
    const filterType = FILTER_TYPE_MAP[filterKey];
    const clearValue = defaults[filterKey];

    setLocalFilters((prev) => ({ ...prev, [filterKey]: clearValue }));
    if (filterType) {
      onFilterChange(filterType, clearValue);
      onApplyFilters();
    }
  }, [FILTER_TYPE_MAP, onApplyFilters, onFilterChange]);

  // Global shortcut: Shift+Enter to apply
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        handleApplyFilters();
      }
    };
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleApplyFilters]);

  const renderFilterSummary = useMemo(() => {
    return () => {
      const filterTags = [];

      if (searchText && String(searchText).trim()) {
        filterTags.push(
          <Tag key="search" color="blue" closable onClose={() => handleRemoveFilterTag('searchText')}>
            Search: {String(searchText).trim()}
          </Tag>
        );
      }

      if (selectedDateRange && Array.isArray(selectedDateRange) && selectedDateRange.length === 2) {
        filterTags.push(
          <Tag key="dateRange" color="orange" closable onClose={() => handleRemoveFilterTag('selectedDateRange')}>
            Date: {dayjs(selectedDateRange[0]).format('DD/MM/YYYY')} - {dayjs(selectedDateRange[1]).format('DD/MM/YYYY')}
          </Tag>
        );
      }

      if (selectedStore && Array.isArray(selectedStore) && selectedStore.length > 0) {
        const storeNames = selectedStore.map(id => {
          const store = shopifyStores?.find((s) => String(s.id) === String(id));
          return store?.storeName || store?.shopName || id;
        }).join(', ');
        filterTags.push(
          <Tag key="store" color="purple" closable onClose={() => handleRemoveFilterTag('selectedStore')}>
            Store: {storeNames}
          </Tag>
        );
      }

      if (selectedFinancialStatus && Array.isArray(selectedFinancialStatus) && selectedFinancialStatus.length > 0) {
        filterTags.push(
          <Tag key="financial" color="green" closable onClose={() => handleRemoveFilterTag('selectedFinancialStatus')}>
            Financial: {selectedFinancialStatus.join(', ')}
          </Tag>
        );
      }

      if (selectedFulfillmentStatus && Array.isArray(selectedFulfillmentStatus) && selectedFulfillmentStatus.length > 0) {
        filterTags.push(
          <Tag key="fulfillment" color="volcano" closable onClose={() => handleRemoveFilterTag('selectedFulfillmentStatus')}>
            Fulfillment: {selectedFulfillmentStatus.join(', ')}
          </Tag>
        );
      }

      if (selectedShipmentStatus && Array.isArray(selectedShipmentStatus) && selectedShipmentStatus.length > 0) {
        const shipmentLabels = {
          info_received: 'Info. Received',
          in_transit: 'In Transit',
          out_for_delivery: 'Out for Delivery',
          failed_attempt: 'Failed Attempt',
          available_for_pickup: 'To Pick Up',
          delivered: 'Delivered',
          exception: 'Exception',
          pending: 'Pending'
        };
        const shipmentText = selectedShipmentStatus.map((status) => shipmentLabels[status] || status).join(', ');
        filterTags.push(
          <Tag key="shipmentStatus" color="geekblue" closable onClose={() => handleRemoveFilterTag('selectedShipmentStatus')}>
            Shipment: {shipmentText}
          </Tag>
        );
      }

      if (selectedShipmentDetail && selectedShipmentDetail !== 'all') {
        const shipmentDetailLabels = {
          stale_over_3_days: 'Quá 3 ngày không cập nhật'
        };
        filterTags.push(
          <Tag key="shipmentDetail" color="red" closable onClose={() => handleRemoveFilterTag('selectedShipmentDetail')}>
            Shipment Details: {shipmentDetailLabels[selectedShipmentDetail] || selectedShipmentDetail}
          </Tag>
        );
      }

      if (selectedConfirmShipment && selectedConfirmShipment !== 'all') {
        const confirmShipmentLabels = {
          confirmed: 'Confirmed',
          pending: 'Pending',
          null: 'Not Set'
        };
        filterTags.push(
          <Tag key="confirmShipment" color="green" closable onClose={() => handleRemoveFilterTag('selectedConfirmShipment')}>
            Confirm Shipment: {confirmShipmentLabels[selectedConfirmShipment] || selectedConfirmShipment}
          </Tag>
        );
      }

      if (selectedCategory && Array.isArray(selectedCategory) && selectedCategory.length > 0) {
        const catNames = selectedCategory.map(id => {
          const category = shopifyCategories?.find((c) => String(c.id) === String(id));
          return category?.name || id;
        }).join(', ');
        filterTags.push(
          <Tag key="category" color="magenta" closable onClose={() => handleRemoveFilterTag('selectedCategory')}>
            Category: {catNames}
          </Tag>
        );
      }

      if (selectedCollection && Array.isArray(selectedCollection) && selectedCollection.length > 0) {
        const collectionNames = selectedCollection.map(id => {
          const collection = shopifyCollections?.find((c) => String(c.shopifyCollectionId) === String(id));
          return collection?.title || collection?.handle || id;
        }).join(', ');
        filterTags.push(
          <Tag key="collection" color="geekblue" closable onClose={() => handleRemoveFilterTag('selectedCollection')}>
            Collection: {collectionNames}
          </Tag>
        );
      }

      if (selectedPrinter && Array.isArray(selectedPrinter) && selectedPrinter.length > 0) {
        const printerNames = selectedPrinter.map(id => {
          const printer = printers?.find((p) => String(p.id) === String(id));
          return printer?.name || id;
        }).join(', ');
        filterTags.push(
          <Tag key="printer" color="geekblue" closable onClose={() => handleRemoveFilterTag('selectedPrinter')}>
            Printer: {printerNames}
          </Tag>
        );
      }

      if (selectedSupporter && Array.isArray(selectedSupporter) && selectedSupporter.length > 0) {
        const supporterNames = selectedSupporter.map(id => {
          const user = users?.find((u) => String(u.id) === String(id));
          return user?.fullName || user?.username || id;
        }).join(', ');
        filterTags.push(
          <Tag key="supporter" color="lime" closable onClose={() => handleRemoveFilterTag('selectedSupporter')}>
            Supporter: {supporterNames}
          </Tag>
        );
      }

      if (selectedTitle && String(selectedTitle).trim()) {
        filterTags.push(
          <Tag key="title" color="cyan" closable onClose={() => handleRemoveFilterTag('selectedTitle')}>
            Title: {String(selectedTitle).trim()}
          </Tag>
        );
      }

      if (selectedSku && String(selectedSku).trim()) {
        filterTags.push(
          <Tag key="sku" color="gold" closable onClose={() => handleRemoveFilterTag('selectedSku')}>
            SKU: {String(selectedSku).trim()}
          </Tag>
        );
      }

      if (selectedEmployeeId && String(selectedEmployeeId).trim()) {
        filterTags.push(
          <Tag
            key="employeeId"
            color="purple"
            closable={!lockedEmployeeId}
            onClose={!lockedEmployeeId ? () => handleRemoveFilterTag('selectedEmployeeId') : undefined}
            icon={lockedEmployeeId ? <LockOutlined /> : undefined}
          >
            Employee ID: {String(selectedEmployeeId).trim()}
          </Tag>
        );
      }

      if (selectedExcludeTitle && String(selectedExcludeTitle).trim()) {
        filterTags.push(
          <Tag key="excludeTitle" color="red" closable onClose={() => handleRemoveFilterTag('selectedExcludeTitle')}>
            Exclude Title: {String(selectedExcludeTitle).trim()}
          </Tag>
        );
      }

      if (selectedExcludeSku && String(selectedExcludeSku).trim()) {
        filterTags.push(
          <Tag key="excludeSku" color="red" closable onClose={() => handleRemoveFilterTag('selectedExcludeSku')}>
            Exclude SKU: {String(selectedExcludeSku).trim()}
          </Tag>
        );
      }

      const basecostLabels = {
        'null': 'Not Set (null)',
        'zero': 'Zero ($0)',
        'null_or_zero': 'Null or Zero',
        'has_value': 'Has Value (> $0)'
      };
      if (selectedBasecost && selectedBasecost !== 'all') {
        filterTags.push(
          <Tag key="basecost" color="cyan" closable onClose={() => handleRemoveFilterTag('selectedBasecost')}>
            Base Cost: {basecostLabels[selectedBasecost] || selectedBasecost}
          </Tag>
        );
      }

      return filterTags;
    };
  }, [
    searchText,
    selectedDateRange,
    selectedStore,
    selectedFinancialStatus,
    selectedFulfillmentStatus,
    selectedShipmentStatus,
    selectedShipmentDetail,
    selectedConfirmShipment,
    selectedCategory,
    selectedCollection,
    selectedPrinter,
    selectedSupporter,
    selectedTitle,
    selectedSku,
    selectedEmployeeId,
    selectedExcludeTitle,
    selectedExcludeSku,
    selectedBasecost,
    shopifyStores,
    shopifyCategories,
    shopifyCollections,
    printers,
    users,
    lockedEmployeeId,
    handleRemoveFilterTag
  ]);

  return (
    <div style={{ paddingBottom: 10, borderBottom: '1px solid #f0f0f0' }}>
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <Row>
            <Col span={6}>
              <SearchInput
                value={localFilters.searchText}
                onChange={(v) => handleLocalChange('searchText', v)}
                onSearch={handleSearchExecute}
                onClear={handleClearSearch}
                loading={loading}
                placeholder="Search by order name, email, SKU, product title... (Shift+Enter to apply)"
              />
              {hasUnappliedChanges && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    fontStyle: 'italic',
                    color: '#fa8c16'
                  }}
                >
                  You have unsaved filter changes. Click "Apply Filters" to search.
                </Text>
              )}
            </Col>
          </Row>
        </Col>

        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => onReload?.({ preserveSelection: true })}
              loading={loading}
              title="Reload with current filters"
            >
              Reload
            </Button>
            <Button
              type={isFilterExpanded ? 'primary' : 'default'}
              icon={<FilterOutlined />}
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
            >
              Filters
              {activeFiltersCount > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    backgroundColor: '#ff4d4f',
                    color: 'white',
                    borderRadius: '50%',
                    width: 18,
                    height: 18,
                    display: 'inline-block',
                    textAlign: 'center',
                    fontSize: 12,
                    lineHeight: '18px'
                  }}
                >
                  {activeFiltersCount}
                </span>
              )}
              {hasUnappliedChanges && (
                <span
                  style={{
                    marginLeft: 4,
                    backgroundColor: '#fa8c16',
                    color: 'white',
                    borderRadius: '50%',
                    width: 6,
                    height: 6,
                    display: 'inline-block'
                  }}
                  title="You have unsaved changes"
                />
              )}
              {isFilterExpanded ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
            </Button>

            {activeFiltersCount > 0 && (
              <Button icon={<ClearOutlined />} onClick={handleClearFilters} title="Clear all filters">
                Clear Filters
              </Button>
            )}

            <Button type="primary" icon={<FilterOutlined />} onClick={handleApplyFilters} loading={loading}>
              Apply Filters
            </Button>
          </Space>
        </Col>
      </Row>

      {activeFiltersCount > 0 && !isFilterExpanded && (
        <Row style={{ marginTop: 12 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
              Active filters:
            </Text>
            <Space size={4} wrap>
              {renderFilterSummary()}
            </Space>
          </Col>
        </Row>
      )}

      {isFilterExpanded && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px dashed #f0f0f0' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedDateRange ? '#1890ff' : undefined }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Purchase Date
                </Text>
                {localFilters.selectedDateRange && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedDateRange')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear date range"
                  />
                )}
              </div>
              <RangePicker
                style={{ width: '100%' }}
                value={localFilters.selectedDateRange}
                onChange={(dates) => handleLocalChange('selectedDateRange', dates)}
                format="YYYY-MM-DD"
                placeholder={['Start Date', 'End Date']}
                allowClear
              />
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedStore && Array.isArray(localFilters.selectedStore) && localFilters.selectedStore.length > 0 ? '#1890ff' : undefined }}>
                  <ShopOutlined style={{ marginRight: 4 }} />
                  Store
                </Text>
                {localFilters.selectedStore && Array.isArray(localFilters.selectedStore) && localFilters.selectedStore.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedStore')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear store"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedStore) ? localFilters.selectedStore : []}
                onChange={(v) => handleLocalChange('selectedStore', v)}
                showSearch
                optionFilterProp="children"
                placeholder="Select stores (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                {shopifyStores.map((store) => (
                  <Option key={store.id} value={store.id}>
                    {store.storeName || store.shopName || store.shopifyDomain || store.id}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedFinancialStatus && Array.isArray(localFilters.selectedFinancialStatus) && localFilters.selectedFinancialStatus.length > 0 ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Financial Status
                </Text>
                {localFilters.selectedFinancialStatus && Array.isArray(localFilters.selectedFinancialStatus) && localFilters.selectedFinancialStatus.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedFinancialStatus')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear financial status"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedFinancialStatus) ? localFilters.selectedFinancialStatus : []}
                onChange={(v) => handleLocalChange('selectedFinancialStatus', v)}
                placeholder="Select financial status (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                <Option value="PAID">Paid</Option>
                <Option value="AUTHORIZED">Authorized</Option>
                <Option value="PARTIALLY_PAID">Partially Paid</Option>
                <Option value="PARTIALLY_REFUNDED">Partially Refunded</Option>
                <Option value="REFUNDED">Refunded</Option>
                <Option value="PENDING">Pending</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedFulfillmentStatus && Array.isArray(localFilters.selectedFulfillmentStatus) && localFilters.selectedFulfillmentStatus.length > 0 ? '#1890ff' : undefined }}>
                  <TruckOutlined style={{ marginRight: 4 }} />
                  Fulfillment Status
                </Text>
                {localFilters.selectedFulfillmentStatus && Array.isArray(localFilters.selectedFulfillmentStatus) && localFilters.selectedFulfillmentStatus.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedFulfillmentStatus')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear fulfillment status"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedFulfillmentStatus) ? localFilters.selectedFulfillmentStatus : []}
                onChange={(v) => handleLocalChange('selectedFulfillmentStatus', v)}
                placeholder="Select fulfillment status (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                <Option value="Unfulfilled">Unfulfilled</Option>
                <Option value="Submitted">Submitted</Option>
                <Option value="Fulfilled">Fulfilled</Option>
                <Option value="Shipped">Shipped</Option>
                <Option value="Canceled">Canceled</Option>
                <Option value="Failed">Failed</Option>
                <Option value="partial">Partial (Shopify)</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedShipmentStatus && Array.isArray(localFilters.selectedShipmentStatus) && localFilters.selectedShipmentStatus.length > 0 ? '#1890ff' : undefined }}>
                  <TruckOutlined style={{ marginRight: 4 }} />
                  Shipment Status
                </Text>
                {localFilters.selectedShipmentStatus && Array.isArray(localFilters.selectedShipmentStatus) && localFilters.selectedShipmentStatus.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedShipmentStatus')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear shipment status filter"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedShipmentStatus) ? localFilters.selectedShipmentStatus : []}
                onChange={(v) => handleLocalChange('selectedShipmentStatus', v)}
                placeholder="Select shipment status (multiple allowed)"
                allowClear
                maxTagCount="responsive"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="info_received">Info. Received</Option>
                <Option value="in_transit">In Transit</Option>
                <Option value="out_for_delivery">Out for Delivery</Option>
                <Option value="failed_attempt">Failed Attempt</Option>
                <Option value="available_for_pickup">To Pick Up</Option>
                <Option value="delivered">Delivered</Option>
                <Option value="exception">Exception</Option>
                <Option value="pending">Pending</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedShipmentDetail && localFilters.selectedShipmentDetail !== 'all' ? '#1890ff' : undefined }}>
                  <TruckOutlined style={{ marginRight: 4 }} />
                  Shipment Details
                </Text>
                {localFilters.selectedShipmentDetail && localFilters.selectedShipmentDetail !== 'all' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedShipmentDetail')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear shipment details filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                value={localFilters.selectedShipmentDetail || 'all'}
                onChange={handleShipmentDetailChange}
                placeholder="Select shipment details"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="all">All Details</Option>
                <Option value="stale_over_3_days">Quá 3 ngày không cập nhật</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedConfirmShipment && localFilters.selectedConfirmShipment !== 'all' ? '#1890ff' : undefined }}>
                  <CheckOutlined style={{ marginRight: 4, color: '#52c41a' }} />
                  Confirm Shipment
                </Text>
                {localFilters.selectedConfirmShipment && localFilters.selectedConfirmShipment !== 'all' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedConfirmShipment')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear confirm shipment filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                value={localFilters.selectedConfirmShipment || 'all'}
                onChange={(v) => handleLocalChange('selectedConfirmShipment', v)}
                placeholder="Select confirmation status"
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                <Option value="all">All Orders</Option>
                <Option value="confirmed">Confirmed</Option>
                <Option value="pending">Pending</Option>
                <Option value="null">Not Set</Option>
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedCategory && Array.isArray(localFilters.selectedCategory) && localFilters.selectedCategory.length > 0 ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Category
                </Text>
                {localFilters.selectedCategory && Array.isArray(localFilters.selectedCategory) && localFilters.selectedCategory.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedCategory')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear category"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedCategory) ? localFilters.selectedCategory : []}
                onChange={(v) => handleLocalChange('selectedCategory', v)}
                showSearch
                optionFilterProp="children"
                placeholder="Select categories (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                {shopifyCategories.map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedCollection && Array.isArray(localFilters.selectedCollection) && localFilters.selectedCollection.length > 0 ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Collection
                </Text>
                {localFilters.selectedCollection && Array.isArray(localFilters.selectedCollection) && localFilters.selectedCollection.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedCollection')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear collection"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedCollection) ? localFilters.selectedCollection : []}
                onChange={(v) => handleLocalChange('selectedCollection', v)}
                showSearch
                optionFilterProp="children"
                placeholder="Select collections (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                {shopifyCollections.map((collection) => (
                  <Option key={`${collection.shopifyStoreId}-${collection.shopifyCollectionId}`} value={collection.shopifyCollectionId}>
                    {collection.title || collection.handle || collection.shopifyCollectionId}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedPrinter && Array.isArray(localFilters.selectedPrinter) && localFilters.selectedPrinter.length > 0 ? '#1890ff' : undefined }}>
                  <PrinterOutlined style={{ marginRight: 4 }} />
                  Printer
                </Text>
                {localFilters.selectedPrinter && Array.isArray(localFilters.selectedPrinter) && localFilters.selectedPrinter.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedPrinter')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear printer"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedPrinter) ? localFilters.selectedPrinter : []}
                onChange={(v) => handleLocalChange('selectedPrinter', v)}
                showSearch
                optionFilterProp="children"
                placeholder="Select printers (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                {printers
                  // Hide inactive printers, but keep ones already selected so their tag still shows a name
                  .filter((printer) => printer.isActive || (Array.isArray(localFilters.selectedPrinter) && localFilters.selectedPrinter.includes(printer.id)))
                  .map((printer) => (
                  <Option key={printer.id} value={printer.id}>
                    {printer.name}
                  </Option>
                ))}
              </Select>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedSupporter && Array.isArray(localFilters.selectedSupporter) && localFilters.selectedSupporter.length > 0 ? '#1890ff' : undefined }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  Supporter
                </Text>
                {localFilters.selectedSupporter && Array.isArray(localFilters.selectedSupporter) && localFilters.selectedSupporter.length > 0 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedSupporter')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear supporter"
                  />
                )}
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                value={Array.isArray(localFilters.selectedSupporter) ? localFilters.selectedSupporter : []}
                onChange={(v) => handleLocalChange('selectedSupporter', v)}
                showSearch
                optionFilterProp="children"
                placeholder="Select supporters (multiple allowed)"
                allowClear
                maxTagCount="responsive"
              >
                {users
                  .filter((u) => u.role === 'Support' || u.role === 'Manager')
                  .map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.username || u.fullName}
                    </Option>
                  ))}
              </Select>
            </Col>
          </Row>

          {/* Second Row: Text Filters */}
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {/* Product Title Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedTitle && localFilters.selectedTitle.trim() ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Product Title
                </Text>
                {localFilters.selectedTitle && localFilters.selectedTitle.trim() && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedTitle')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear title filter"
                  />
                )}
              </div>
              <Input
                style={{ width: '100%' }}
                onChange={(e) => handleLocalChange('selectedTitle', e.target.value)}
                value={localFilters.selectedTitle}
                placeholder="Search by product title"
                allowClear
              />
            </Col>

            {/* SKU Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedSku && localFilters.selectedSku.trim() ? '#1890ff' : undefined }}>
                  <MenuOutlined style={{ marginRight: 4 }} />
                  SKU
                </Text>
                {localFilters.selectedSku && localFilters.selectedSku.trim() && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedSku')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear SKU filter"
                  />
                )}
              </div>
              <Input
                style={{ width: '100%' }}
                onChange={(e) => handleLocalChange('selectedSku', e.target.value)}
                value={localFilters.selectedSku}
                placeholder="Search by SKU"
                allowClear
              />
            </Col>

            {/* Employee ID Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedEmployeeId && localFilters.selectedEmployeeId.trim() ? '#1890ff' : undefined }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  Employee ID
                  {lockedEmployeeId && <LockOutlined style={{ marginLeft: 4, color: '#faad14' }} />}
                </Text>
                {!lockedEmployeeId && localFilters.selectedEmployeeId && localFilters.selectedEmployeeId.trim() && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedEmployeeId')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear employee ID filter"
                  />
                )}
              </div>
              <Tooltip title={lockedEmployeeId ? 'Bộ lọc Employee ID được khóa theo tài khoản của bạn' : undefined}>
                <Input
                  style={{ width: '100%' }}
                  onChange={(e) => !lockedEmployeeId && handleLocalChange('selectedEmployeeId', e.target.value)}
                  value={localFilters.selectedEmployeeId}
                  placeholder={lockedEmployeeId ? `Được khóa: ${lockedEmployeeId}` : 'Search by Employee ID'}
                  allowClear={!lockedEmployeeId}
                  disabled={!!lockedEmployeeId}
                  suffix={lockedEmployeeId ? <LockOutlined style={{ color: '#faad14' }} /> : undefined}
                />
              </Tooltip>
            </Col>

            {/* Exclude Title Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedExcludeTitle && localFilters.selectedExcludeTitle.trim() ? '#ff4d4f' : undefined }}>
                  <CloseCircleOutlined style={{ marginRight: 4 }} />
                  Exclude Title
                </Text>
                {localFilters.selectedExcludeTitle && localFilters.selectedExcludeTitle.trim() && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedExcludeTitle')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear exclude title filter"
                  />
                )}
              </div>
              <Input
                style={{ width: '100%' }}
                onChange={(e) => handleLocalChange('selectedExcludeTitle', e.target.value)}
                value={localFilters.selectedExcludeTitle}
                placeholder="Exclude products with title"
                allowClear
              />
            </Col>

            {/* Exclude SKU Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedExcludeSku && localFilters.selectedExcludeSku.trim() ? '#ff4d4f' : undefined }}>
                  <CloseCircleOutlined style={{ marginRight: 4 }} />
                  Exclude SKU
                </Text>
                {localFilters.selectedExcludeSku && localFilters.selectedExcludeSku.trim() && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedExcludeSku')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear exclude SKU filter"
                  />
                )}
              </div>
              <Input
                style={{ width: '100%' }}
                onChange={(e) => handleLocalChange('selectedExcludeSku', e.target.value)}
                value={localFilters.selectedExcludeSku}
                placeholder="Exclude products with SKU"
                allowClear
              />
            </Col>

            {/* Base Cost Filter */}
            <Col xs={24} sm={12} md={6}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ color: localFilters.selectedBasecost && localFilters.selectedBasecost !== 'all' ? '#1890ff' : undefined }}>
                  <TagOutlined style={{ marginRight: 4 }} />
                  Base Cost
                </Text>
                {localFilters.selectedBasecost && localFilters.selectedBasecost !== 'all' && (
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    onClick={() => clearIndividualFilter('selectedBasecost')}
                    style={{ color: '#999', padding: 0, minWidth: 'auto' }}
                    title="Clear base cost filter"
                  />
                )}
              </div>
              <Select
                style={{ width: '100%' }}
                value={localFilters.selectedBasecost || 'all'}
                onChange={(v) => handleLocalChange('selectedBasecost', v)}
                placeholder="Filter by base cost"
              >
                <Option value="all">All Orders</Option>
                <Option value="null">Not Set (null)</Option>
                <Option value="zero">Zero ($0)</Option>
                <Option value="null_or_zero">Null or Zero</Option>
                <Option value="has_value">Has Value (&gt; $0)</Option>
              </Select>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default ShopifyOrdersFilter;
